import React from 'react';
import Markdown from 'react-markdown';

interface FormattedMarkdownProps {
  content: string;
  className?: string;
}

export const FormattedMarkdown: React.FC<FormattedMarkdownProps> = ({ content, className = '' }) => {
  if (!content) return null;

  return (
    <div className={`formatted-markdown-container text-xs leading-relaxed break-words space-y-2.5 ${className}`}>
      <Markdown
        components={{
          h1: ({ node, ...props }) => (
            <h1 className="text-sm font-bold text-white mt-3 mb-1.5 border-b border-[#2A2F31] pb-1" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className="text-xs font-bold text-white mt-2.5 mb-1 text-[#14B8A6]" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className="text-xs font-semibold text-gray-100 mt-2 mb-1" {...props} />
          ),
          p: ({ node, ...props }) => (
            <p className="leading-relaxed mb-2 last:mb-0" {...props} />
          ),
          strong: ({ node, ...props }) => (
            <strong className="font-bold text-white tracking-wide" {...props} />
          ),
          em: ({ node, ...props }) => (
            <em className="italic text-gray-300" {...props} />
          ),
          ul: ({ node, ...props }) => (
            <ul className="list-disc list-outside pl-4 space-y-1.5 my-2" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal list-outside pl-4 space-y-1.5 my-2" {...props} />
          ),
          li: ({ node, ...props }) => (
            <li className="leading-relaxed pl-0.5" {...props} />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote className="border-l-2 border-[#14B8A6] pl-3 py-0.5 my-2 italic text-gray-300 bg-[#14B8A6]/5 rounded-r" {...props} />
          ),
          code: ({ node, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const isInline = !match && !String(children).includes('\n');
            if (isInline) {
              return (
                <code className="px-1.5 py-0.5 rounded bg-[#20272A] text-[#14B8A6] font-mono text-[11px]" {...props}>
                  {children}
                </code>
              );
            }
            return (
              <pre className="p-3 rounded-xl bg-[#090B0C] border border-[#20272A] overflow-x-auto text-[11px] font-mono text-gray-200 my-2">
                <code {...props}>{children}</code>
              </pre>
            );
          },
          a: ({ node, ...props }) => (
            <a
              className="text-[#14B8A6] hover:text-[#0EA5A4] underline underline-offset-2 font-medium transition-colors cursor-pointer"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            />
          ),
          hr: ({ node, ...props }) => (
            <hr className="my-3 border-[#2A2F31]" {...props} />
          )
        }}
      >
        {content}
      </Markdown>
    </div>
  );
};
